import { useState, useEffect, useRef } from 'react';

/**
 * Hook that detects when a loading state exceeds a timeout.
 * Returns { timedOut, retry } where timedOut flips to true after `ms` milliseconds
 * of `isLoading` being true, and retry() resets the timeout.
 *
 * Usage:
 *   const { timedOut, retry } = useLoadingTimeout(loading, 30000);
 *   if (timedOut) return <p>Something went wrong. <button onClick={retry}>Retry</button></p>;
 */
export default function useLoadingTimeout(isLoading, ms = 30000) {
  const [timedOut, setTimedOut] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (isLoading) {
      setTimedOut(false);
      timerRef.current = setTimeout(() => setTimedOut(true), ms);
    } else {
      setTimedOut(false);
      if (timerRef.current) clearTimeout(timerRef.current);
    }
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [isLoading, ms, retryCount]);

  const retry = () => {
    setTimedOut(false);
    setRetryCount((c) => c + 1);
  };

  return { timedOut, retry, retryCount };
}
